import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { PrismicRichText } from "@prismicio/react";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, Configure, useInstantSearch, SearchBox, RefinementList } from "react-instantsearch";

const searchClient = algoliasearch(
  "G0VLPZK1K5",
  "1d5ab2118d90a0ca1794dc8c22845f51"
);

// Custom component to filter past webinars client-side with pagination
function PastWebinarsHits({ hitComponent }) {
  const { results } = useInstantSearch();
  const [currentPage, setCurrentPage] = React.useState(0);
  const webinarsPerPage = 9;
  
  // Get today's timestamp for filtering past webinars
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayValue = Math.floor(today.valueOf() / 1000);

  // Debug logging (remove in production)
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('All webinars debug:', {
      totalHits: results?.hits?.length || 0,
      todayValue,
      todayDate: new Date(todayValue * 1000).toDateString(),
      sampleHit: results?.hits?.[0]
    });
    
    // Log each webinar for debugging
    results?.hits?.forEach((hit, index) => {
      console.log(`Webinar ${index + 1}:`, {
        title: hit.data?.resource_title?.text,
        date_published: hit.data?.date_published,
        date_timestamp: hit.data?.date_timestamp,
        resource_type: hit.data?.resource_type,
      });
    });
  }
  
  // Filter for past webinars client-side
  const pastWebinars = results?.hits?.filter(event => {
    // All hits should already be webinars from All_webinars index
    // Use date_timestamp (Unix timestamp in seconds) for comparison
    if (event.data.date_timestamp) {
      const eventTimestamp = parseInt(event.data.date_timestamp);
      return eventTimestamp < todayValue;
    }
    return false;
  }) || [];

  // Calculate pagination
  const totalPages = Math.ceil(pastWebinars.length / webinarsPerPage);
  const startIndex = currentPage * webinarsPerPage;
  const endIndex = startIndex + webinarsPerPage;
  const currentWebinars = pastWebinars.slice(startIndex, endIndex);

  if (pastWebinars.length === 0) {
    // If no past webinars but we have hits, show a different message for debugging
    if (results?.hits?.length > 0) {
      return (
        <div>
          <p className="text-lg flex items-center gap-4 px-5 py-2 bg-yellow w-fit rounded-xl mb-4">
            <i className="fa-regular fa-calendar text-2xl text-orange" />
            <span>Found {results.hits.length} webinars but none are past events. Check console for details.</span>
          </p>
          {/* Show all webinars for debugging */}
          <div className="ais-Hits">
            <ol className="ais-Hits-list">
              {results.hits.slice(0, 3).map((hit, index) => {
                const HitComponent = hitComponent;
                return (
                  <li key={hit.objectID || index} className="ais-Hits-item">
                    <HitComponent hit={hit} />
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      );
    }
    
    return (
      <p className="text-lg flex items-center gap-4 px-5 py-2 bg-grey-light w-fit rounded-xl">
        <i className="fa-regular fa-calendar-xmark text-2xl text-red" />
        <span>No webinars found in Algolia index.</span>
      </p>
    );
  }

  return (
    <div>
      <div className="ais-Hits">
        <ol className="ais-Hits-list">
          {currentWebinars.map((hit, index) => {
            const HitComponent = hitComponent;
            return (
              <li key={hit.objectID || index} className="ais-Hits-item">
                <HitComponent hit={hit} />
              </li>
            );
          })}
        </ol>
      </div>
      
      {/* Custom Pagination matching original style */}
      {totalPages > 1 && (
        <nav className="ais-Pagination font-bold text-xl" style={{padding: '1rem 0'}}>
          <ul className="ais-Pagination-list">
            <li className={`ais-Pagination-item ais-Pagination-item--previousPage ${currentPage === 0 ? 'ais-Pagination-item--disabled' : ''}`}>
              <a 
                className={`ais-Pagination-link ${currentPage === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={currentPage > 0 ? () => setCurrentPage(currentPage - 1) : undefined}
              >
                ‹
              </a>
            </li>
            
            {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
              let pageNum;
              if (totalPages <= 3) {
                pageNum = i;
              } else {
                // Show current page and one on each side when possible
                if (currentPage === 0) {
                  pageNum = i; // Show 0, 1, 2
                } else if (currentPage === totalPages - 1) {
                  pageNum = totalPages - 3 + i; // Show last 3 pages
                } else {
                  pageNum = currentPage - 1 + i; // Show current-1, current, current+1
                }
              }
              
              return (
                <li key={pageNum} className={`ais-Pagination-item ais-Pagination-item--page ${pageNum === currentPage ? 'ais-Pagination-item--selected' : ''}`}>
                  <a 
                    className={`ais-Pagination-link ${pageNum === currentPage ? 'bg-accessible-green text-white rounded' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum + 1}
                  </a>
                </li>
              );
            })}
            
            <li className={`ais-Pagination-item ais-Pagination-item--nextPage ${currentPage === totalPages - 1 ? 'ais-Pagination-item--disabled' : ''}`}>
              <a 
                className={`ais-Pagination-link ${currentPage === totalPages - 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={currentPage < totalPages - 1 ? () => setCurrentPage(currentPage + 1) : undefined}
              >
                ›
              </a>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

// WebinarCard component similar to EventCard
const WebinarCard = ({ hit }) => {
  return (
    <a href={hit.url} className="flex flex-col justify-between w-auto">
      <span>
        <span className="relative block">
          <GatsbyImage 
            image={getImage(hit.data.meta_image)} 
            alt={hit.data.meta_image?.alt || `${hit.data.resource_title.text} graphic`} 
            className="rounded-t-lg" 
          />
          
        </span>
        <span className="p-7 block">
          <span className="bg-grey-light py-1 px-2 rounded text-sm font-bold mb-3 inline-block">
            {hit.data.date_published}
          </span>
          <span className="title text-xl font-bold pr-6 block leading-tight">
            {hit.data.resource_title.text}
          </span>
          <p className="mb-0 mt-4">{hit.data.description.text}</p>
        </span>
      </span>
      <span className="pb-7 pr-7 pl-7 block">
        <div className="btn text-sm bg-yellow">Watch recording</div>
      </span>
    </a>
  );
};

// NoResultsBoundary component
function NoResultsBoundary({ children, fallback }) {
  const { results } = useInstantSearch();

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return children;
}

function NoResults() {
  const { indexUiState } = useInstantSearch();

  return (
    <div>
      <p>
        No results for <q>{indexUiState.query}</q>.
      </p>
    </div>
  );
}

export const AllWebinars = (props) => {
  const sc = props.slice.primary;

  let space = sc.spacing;

  if (space === "Both large") {
    space = "gutter";
  } else if (space === "Both small") {
    space = "gutter sm";
  } else if (space === "Top large") {
    space = "gutter bot";
  } else if (space === "Top small") {
    space = "gutter bot sm";
  } else if (space === "Bottom large") {
    space = "gutter top";
  } else {
    space = "gutter top sm";
  }

  let bg = sc.background === "gradient" ? "gradient-to-b from-grey-light to-white" : sc.background === "grey" ? "grey-light" : sc.background;

  return (
    <section className={"relative bg-" + bg + " " + space}>
      <div className="wrapper all-resources">
        {sc.section_title?.text ? (
          <div className="mb-8">
            <PrismicRichText field={sc.section_title.richText} />
          </div>
        ) : (
          <h2 className="mb-8">Past webinars</h2>
        )}
        
        <InstantSearch searchClient={searchClient} indexName="All_webinars" routing={true}>
          
          <Configure 
            hitsPerPage={100}
          />
          <SearchBox placeholder="Search for all resources" insights submitIconComponent={() => (
                            <i className="fa-solid fa-magnifying-glass text-teal text-xl md:text-3xl" />
                        )} resetIconComponent={() => (
                            <></>
                          )} />
                                                     <RefinementList 
                             attribute="tags" 
                             operator="and" 
                             limit={8}
                             transformItems={(items) => 
                               items.filter(item => item.label.toLowerCase() !== 'webinar')
                             }
                           />
                        <NoResultsBoundary fallback={<NoResults />}><PastWebinarsHits hitComponent={WebinarCard} /></NoResultsBoundary>
          
        </InstantSearch>
      </div>
    </section>
  );
};

